import { GoalContextView } from "@/components/flow/goal-context-view";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import {
  usePublicCreatePortfolio,
  usePublicDeletePortfolio,
  usePublicFlowPortfolios,
} from "@/lib/hooks/use-public-flow/use-portfolios";
import type { FlowPage } from "@/lib/types/flow";

type PublicGoalContextProps = {
  goalId: string;
  isCompleted: boolean;
  multiPortfolio: boolean;
  goalPages: FlowPage[];
  portfolioPages: FlowPage[];
  /** QL-120 — goal-page visibility for the goal this panel is drawn for. */
  isGoalPageVisible?: (page: FlowPage, goalId: string) => boolean;
  /** QL-120 — portfolio-page visibility for the active (goal × portfolio) pair. */
  isPortfolioPageVisible?: (page: FlowPage, goalId: string, portfolioId: string) => boolean;
  renderGoalPage: (page: FlowPage, goalId: string) => React.ReactNode;
  renderPortfolioPage: (page: FlowPage, goalId: string, portfolio: any) => React.ReactNode;
  // Present only in the single-goal (hosted-globals, flat top row) case.
  globalPagesBefore?: FlowPage[];
  globalPagesAfter?: FlowPage[];
  renderGlobalPage?: (page: FlowPage) => React.ReactNode;
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
};

/**
 * Public per-goal data wrapper (QP-150). Mirror of {@link AdvisorGoalContext} using
 * public portfolio hooks (token-based API). Fetches one goal's portfolios and wires
 * add/remove actions, then feeds them to the presentational {@link GoalContextView}.
 */
export function PublicGoalContext({
  goalId,
  isCompleted,
  multiPortfolio,
  goalPages,
  portfolioPages,
  isGoalPageVisible,
  isPortfolioPageVisible,
  renderGoalPage,
  renderPortfolioPage,
  globalPagesBefore,
  globalPagesAfter,
  renderGlobalPage,
  showValidationErrors,
  markers,
}: PublicGoalContextProps) {
  const { portfolios, isLoading } = usePublicFlowPortfolios(goalId);
  const createPortfolio = usePublicCreatePortfolio(goalId);
  const deletePortfolio = usePublicDeletePortfolio();

  return (
    <GoalContextView
      goalId={goalId}
      goalPages={goalPages}
      portfolioPages={portfolioPages}
      isGoalPageVisible={isGoalPageVisible}
      isPortfolioPageVisible={isPortfolioPageVisible}
      multiPortfolio={multiPortfolio}
      isCompleted={isCompleted}
      portfolios={portfolios}
      isLoadingPortfolios={isLoading}
      isAddingPortfolio={createPortfolio.isPending}
      onAddPortfolio={(name, onCreated) =>
        createPortfolio.mutate(
          { name },
          {
            onSuccess: (created) => {
              const id = (created as { id?: string } | undefined)?.id;
              if (id) onCreated(id);
            },
          },
        )
      }
      onRemovePortfolio={(portfolioId) => deletePortfolio.mutate({ portfolioId, goalId })}
      renderGoalPage={renderGoalPage}
      renderPortfolioPage={renderPortfolioPage}
      globalPagesBefore={globalPagesBefore}
      globalPagesAfter={globalPagesAfter}
      renderGlobalPage={renderGlobalPage}
      showValidationErrors={showValidationErrors}
      markers={markers}
    />
  );
}
